import { useMemo } from 'react';
import { addDays, isSameDay } from 'date-fns';

import { getCustomFormatDate } from '@kc-console/core';
import { createColumnHelper, NameCell } from '@kc-console/shared-ui';

import ServiceStatusCell from './ServiceStatusCell';

import type { Service } from '../model/types';

export const SERVICE_STATUS_COLUMN_COUNT = 7;
const STATUS_COLUMN_SIZE = 120;

const columnHelper = createColumnHelper<Service>();

interface UseServiceStatusColumnsParams {
  statusHeaderLabels?: string[];
}

export const getServiceStatusHeadCellLabel = (date?: string) => {
  if (!date) return '-';

  const target = new Date(date);
  if (isSameDay(target, new Date())) {
    return '오늘';
  }

  return getCustomFormatDate(date, 'MM.dd (EEE)');
};

/**
 * 서비스 상태 테이블의 컬럼 정의 훅.
 * @description
 * - 서비스명 컬럼 + 오늘부터 과거 7일의 상태 컬럼 + 빈 공간 컬럼으로 구성된다.
 * @example
 * const columns = useServiceStatusColumns();
 */
const useServiceStatusColumns = ({
  statusHeaderLabels = [],
}: UseServiceStatusColumnsParams = {}) => {
  return useMemo(() => {
    const today = new Date();
    const statusColumns = Array.from({ length: SERVICE_STATUS_COLUMN_COUNT }, (_, index) => {
      const fallbackDate = addDays(today, -index).toISOString();
      return columnHelper.display({
        id: `status-${index}`,
        header: statusHeaderLabels[index] ?? getServiceStatusHeadCellLabel(fallbackDate),
        cell: ({ row }) => <ServiceStatusCell item={row.original.statusList?.[index]} />,
        meta: {
          header: {
            info: index === 0 ? getCustomFormatDate(fallbackDate, 'MM.dd (EEE)') : '',
          },
        },
        size: STATUS_COLUMN_SIZE,
        minSize: STATUS_COLUMN_SIZE,
        maxSize: STATUS_COLUMN_SIZE,
        enableSorting: false,
        enableHiding: index !== 0,
      });
    });

    return [
      columnHelper.accessor('serviceName', {
        id: 'name',
        header: '서비스',
        cell: ({ row: { original } }) => <NameCell name={original.serviceName} />,
        enableSorting: true,
        enableHiding: false,
        size: 240,
      }),
      ...statusColumns,
    ];
  }, [statusHeaderLabels]);
};

export default useServiceStatusColumns;
